import React from 'react';
import { Progress } from '@/components/ui/progress';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const calculateStrength = (pwd: string): { score: number; label: string; color: string } => {
    let score = 0;
    
    // Length score
    if (pwd.length >= 8) score += 10;
    if (pwd.length >= 12) score += 10;
    if (pwd.length >= 16) score += 10;
    
    // Character variety
    if (/[a-z]/.test(pwd)) score += 10;
    if (/[A-Z]/.test(pwd)) score += 10;
    if (/[0-9]/.test(pwd)) score += 10;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 20;
    
    // Pattern complexity
    if (!/(.)\1{2,}/.test(pwd)) score += 10; // No repeated chars
    if (pwd.length > 0 && !/^[a-zA-Z]+$/.test(pwd) && !/^[0-9]+$/.test(pwd)) score += 10;
    
    score = Math.min(100, score);
    
    let label = "Weak";
    let color = "bg-red-500";
    
    if (score >= 90) {
      label = "Very Strong";
      color = "bg-green-600";
    } else if (score >= 70) {
      label = "Strong";
      color = "bg-green-500";
    } else if (score >= 50) {
      label = "Good";
      color = "bg-yellow-500";
    } else if (score >= 30) {
      label = "Fair";
      color = "bg-orange-500";
    }
    
    return { score, label, color };
  };

  if (!password) return null;

  const { score, label, color } = calculateStrength(password);

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">Password Strength:</span>
        <span className={`font-medium ${
          label === 'Very Strong' ? 'text-green-600' :
          label === 'Strong' ? 'text-green-500' :
          label === 'Good' ? 'text-yellow-600' :
          label === 'Fair' ? 'text-orange-600' :
          'text-red-600'
        }`}>{label}</span>
      </div>
      <Progress value={score} className="h-2" />
      
      {score < 70 && (
        <div className="text-xs text-gray-500 space-y-1">
          <p>Password requirements:</p>
          <ul className="list-disc list-inside ml-2">
            <li>At least 12 characters long</li>
            <li>Include uppercase and lowercase letters</li>
            <li>Include numbers and special characters</li>
            <li>Avoid common patterns or repeated characters</li>
          </ul>
        </div>
      )}
    </div>
  );
}